import {BasicDataTable, BasicDataTableConfig, TypedColumnSettings} from "@/shared/ui/basicDataTable"
import {WorkingTimeRequestDialog} from "@/features/requests/workingTimeRequestDialog";
import {WorkingTimeNoticeDialogBridge} from "@/features/requests/workingTimeNoticeDialog/workingTimeNoticeDialogBridge";
import {noticeRequestDuplicateSource} from "@/features/requests/workingTimeNoticeDialog/workingTimeNoticeDialog";
import * as Constants from "@/shared/constants";
import {WorkingTimeRequestUtil} from "@/shared/workingtime/workingTimeRequestUtil";
import {SecurityStatus} from "@/shared/securityStatus";
import {
	AbstractWorkingTimeRequestResponseModel,
	CustomFieldDefinition,
	PartialWorkingTimeType,
	WorkingTimeType
} from "@/shared/timr";
import {WorkingTimeRequestEvent, WorkingTimeRequestStatus} from "@/shared/timrTypes";

export interface WorkingTimeRequestTableConfig extends BasicDataTableConfig {
	isGroupLeader: boolean;
	workingTimeTypes: WorkingTimeType[];
	currentUserId: number;
	isGroupLeaderForThemself: boolean;
	customFieldDefinitions: CustomFieldDefinition[];
	workingTimeNoticeDialog: WorkingTimeNoticeDialogBridge;
}

export class WorkingTimeRequestTable extends BasicDataTable<AbstractWorkingTimeRequestResponseModel> {

	public static readonly EVENT_SELECT = "select";
	public static readonly EVENT_UNSELECT = "unselect";

	private workingTimeRequestDialog: WorkingTimeRequestDialog;

	constructor(public config: WorkingTimeRequestTableConfig) {
		super("/timr/requests/findWorkingTimeRequests.json", config);

		this.init();
	}

	protected createDataTableConfig(): DataTables.Settings {
		return {
			dom: `rt<"datatable-footer"ip>`,
			select: {
				style: "single",
				selector: "td:not(:first-child)",
				info: false
			},
			pageLength: Constants.REPORT_TABLES_PAGE_LENGTH
		};
	}

	protected getColumnSettings(): TypedColumnSettings<AbstractWorkingTimeRequestResponseModel>[] {
		const columns: TypedColumnSettings<AbstractWorkingTimeRequestResponseModel>[] = [];
		columns.push({
			data: "id",
			orderable: false,
			className: "context-menu",
			render: this.contextMenu.render,
			width: BasicDataTable.COLUMN_SIZE_MENU
		});
		columns.push({
			data: "user",
			orderable: true,
			render: BasicDataTable.renderUser,
			visible: this.config.isGroupLeader
		});
		columns.push({data: "workingTimeType", orderable: true, render: this.renderWorkingTimeType});
		columns.push({
			data: "start",
			orderable: true,
			render: this.renderDateRange
		});
		if (SecurityStatus.isSubstitutesAllowed()) {
			// "substitute" only exists on date-span requests, not on the abstract row type shared with minute-based requests.
			// no substitute renders empty
			columns.push({data: "substitute", orderable: false, render: BasicDataTable.renderUser} as TypedColumnSettings<AbstractWorkingTimeRequestResponseModel>);
		}
		columns.push({data: "status", orderable: true, render: this.renderStatus});
		if (SecurityStatus.isProofsAllowed()) {
			columns.push({data: "proofStatus", orderable: true, className: "table-data-center", render: this.renderProofStatus});
		}
		columns.push({
			data: "description",
			className: "table-data-description",
			orderable: true,
			render: (_, t, r, m) => BasicDataTable.renderText(r.description, t, r, m)
		});
		columns.push({
			data: "statusComment",
			className: "table-data-description",
			orderable: true,
			render: BasicDataTable.renderDescriptionText
		});
		return columns;
	}

	protected getDefaultOrder(): (string | number)[][] {
		return [[3, "desc"]];
	}

	protected createContextMenu() {
		return super.createContextMenu()
			.addMenuEntry({
				label: messages["requests.workingTimeRequestsUser.context.show"],
				action: workingTimeRequest => this.showWorkingTimeRequest(workingTimeRequest)
			})
			.addMenuEntry({
				label: messages["requests.workingTimeRequestsUser.context.edit"],
				action: workingTimeRequest => this.editWorkingTimeRequest(workingTimeRequest),
				active: workingTimeRequest => this.editWorkingTimeRequestAllowed(workingTimeRequest)
			})
			.addMenuEntry({
				label: messages["requests.workingTimeRequestsUser.context.duplicate"],
				action: workingTimeRequest => this.duplicateWorkingTimeRequest(workingTimeRequest),
				// A notice request's type may have been disabled or archived since
				active: workingTimeRequest => !WorkingTimeRequestUtil.isWorkingTimeNoticeRequest(workingTimeRequest)
					|| this.config.workingTimeNoticeDialog.hasWorkingTimeType(workingTimeRequest.workingTimeType.id)
			})
			.addMenuEntry({
				label: messages["requests.workingTimeRequestsUser.context.delete"],
				action: workingTimeRequest => this.deleteWorkingTimeRequest(workingTimeRequest),
				active: workingTimeRequest => this.isRequestDeletable(workingTimeRequest)
			})
			.addDivider()
			.addMenuEntry({
				label: messages["requests.workingTimeRequestsUser.context.approve"],
				action: workingTimeRequest => this.approveWorkingTimeRequest(workingTimeRequest),
				show: workingTimeRequest => workingTimeRequest.currentApprovableStep && WorkingTimeRequestUtil.isInApprovalProcess(workingTimeRequest)
			})
			.addMenuEntry({
				label: messages["requests.workingTimeRequestsUser.context.reject"],
				action: workingTimeRequest => this.rejectWorkingTimeRequest(workingTimeRequest),
				show: workingTimeRequest => workingTimeRequest.currentApprovableStep && WorkingTimeRequestUtil.isInApprovalProcess(workingTimeRequest)
			})
			.addMenuEntry({
				label: messages["requests.workingTimeRequestsUser.context.approveImmediately"],
				action: workingTimeRequest => WorkingTimeRequestUtil.approveWorkingTimeRequestImmediately(workingTimeRequest, () => this.reload()),
				show: workingTimeRequest => WorkingTimeRequestUtil.isImmediateStatusChangeAllowed(workingTimeRequest, this.config.isGroupLeaderForThemself) && WorkingTimeRequestUtil.isInApprovalProcess(workingTimeRequest)
			})
			.addMenuEntry({
				label: messages["requests.workingTimeRequestsUser.context.rejectImmediately"],
				action: workingTimeRequest => WorkingTimeRequestUtil.rejectWorkingTimeRequestImmediately(workingTimeRequest, () => this.reload()),
				show: workingTimeRequest => WorkingTimeRequestUtil.isImmediateStatusChangeAllowed(workingTimeRequest, this.config.isGroupLeaderForThemself) && WorkingTimeRequestUtil.isInApprovalProcess(workingTimeRequest)
			})
			.addMenuEntry({
				label: messages["requests.workingTimeRequestsUser.context.requestCancellation"],
				action: workingTimeRequest => this.requestCancellationWorkingTimeRequest(workingTimeRequest),
				show: workingTimeRequest => workingTimeRequest.user.id === globals.userId && workingTimeRequest.status === WorkingTimeRequestStatus.Approved
			})
			.addMenuEntry({
				label: messages["requests.workingTimeRequestsUser.context.approveCancellation"],
				action: workingTimeRequest => this.approveCancellationWorkingTimeRequest(workingTimeRequest),
				show: workingTimeRequest => workingTimeRequest.currentApprovableStep && WorkingTimeRequestUtil.isInCancellationProcess(workingTimeRequest)
			})
			.addMenuEntry({
				label: messages["requests.workingTimeRequestsUser.context.rejectCancellation"],
				action: workingTimeRequest => this.rejectCancellationWorkingTimeRequest(workingTimeRequest),
				show: workingTimeRequest => workingTimeRequest.currentApprovableStep && WorkingTimeRequestUtil.isInCancellationProcess(workingTimeRequest)
			})
			.addMenuEntry({
				label: messages["requests.workingTimeRequestsUser.context.approveCancellationImmediately"],
				action: workingTimeRequest => WorkingTimeRequestUtil.approveCancellationWorkingTimeRequestImmediately(workingTimeRequest, () => this.reload()),
				show: workingTimeRequest => WorkingTimeRequestUtil.isImmediateStatusChangeAllowed(workingTimeRequest, this.config.isGroupLeaderForThemself) && WorkingTimeRequestUtil.isInCancellationProcess(workingTimeRequest)
			})
			.addMenuEntry({
				label: messages["requests.workingTimeRequestsUser.context.rejectCancellationImmediately"],
				action: workingTimeRequest => WorkingTimeRequestUtil.rejectCancellationWorkingTimeRequestImmediately(workingTimeRequest, () => this.reload()),
				show: workingTimeRequest => WorkingTimeRequestUtil.isImmediateStatusChangeAllowed(workingTimeRequest, this.config.isGroupLeaderForThemself) && WorkingTimeRequestUtil.isInCancellationProcess(workingTimeRequest)
			})
			.addMenuEntry({
				label: messages["requests.workingTimeRequestsUser.context.cancelImmediately"],
				action: workingTimeRequest => WorkingTimeRequestUtil.cancelImmediatelyWorkingTimeRequest(workingTimeRequest, () => this.reload()),
				show: workingTimeRequest => WorkingTimeRequestUtil.isImmediateStatusChangeAllowed(workingTimeRequest, this.config.isGroupLeaderForThemself) &&
					workingTimeRequest.status === WorkingTimeRequestStatus.Approved
			});
	}

	/**
	 * A working time request is deletable if it is in an approval process and the request is the current users request
	 * or if the user is an admin and the request is in status "Cancelled".
	 * @param workingTimeRequest The working time request to check
	 * @returns {boolean} True if the request is deletable, false otherwise
	 * @private
	 */
	private isRequestDeletable(workingTimeRequest: AbstractWorkingTimeRequestResponseModel): boolean {
		return (WorkingTimeRequestUtil.isInApprovalProcess(workingTimeRequest) && workingTimeRequest.user.id === globals.userId) ||
			(globals.isAuthorizedForAllUsers && workingTimeRequest.status === WorkingTimeRequestStatus.Cancelled);
	}

	private renderWorkingTimeType(this: void, data: PartialWorkingTimeType, type: string, request: AbstractWorkingTimeRequestResponseModel, meta: DataTables.CellMetaSettings): string {
		if (WorkingTimeRequestUtil.isWorkingTimeNoticeRequest(request)) {
			return data ? `<span class="mr-10">${data.name}</span><span class="badge badge-notice">${messages["requests.workingTimeRequestsUser.notice"]}</span>` : "";
		}
		return data ? data.name : "";
	}

	private renderStatus(this: void, data: WorkingTimeRequestStatus, type: string, request: AbstractWorkingTimeRequestResponseModel, meta: DataTables.CellMetaSettings): string {
		return data ? `<span class="label request-status-${data} request-category-${request.workingTimeType.category}"><span>${messages["workingTimeRequestStatus." + data]}</span></span>` : "";
	}

	private renderProofStatus(this: void, data: string, type: string, request: AbstractWorkingTimeRequestResponseModel, meta: DataTables.CellMetaSettings): string {
		return data ? `<span class="label proof-status status-${data}"><span>${messages["proofStatus." + data]}</span></span>` : "";
	}

	private renderDateRange(this: void, _data: string, _type: string, request: AbstractWorkingTimeRequestResponseModel, _meta: DataTables.CellMetaSettings): string {
		return WorkingTimeRequestUtil.renderDateRange(request);
	}

	private init() {
		this.initWorkingTimeRequestDialog();
	}

	private initWorkingTimeRequestDialog() {
		this.workingTimeRequestDialog = new WorkingTimeRequestDialog({
			dialogId: "#workingTimeRequestDialog",
			workingTimeTypes: this.config.workingTimeTypes,
			isGroupLeaderForThemself: this.config.isGroupLeaderForThemself,
			userSelectionEnabled: this.config.isGroupLeader,
			customFieldDefinitions: this.config.customFieldDefinitions
		});
		$(this.workingTimeRequestDialog).on(WorkingTimeRequestDialog.EVENT_REQUEST_DELETED, () => {
			this.reload();
		}).on(WorkingTimeRequestDialog.EVENT_REQUEST_EDITED, () => {
			this.reload();
		}).on(WorkingTimeRequestDialog.EVENT_REQUEST_STATUS_CHANGED, () => {
			this.reload();
		});

		$(this).on(BasicDataTable.EVENT_DBLCLICK, (_event, workingTimeRequest: AbstractWorkingTimeRequestResponseModel) => {
			if (this.editWorkingTimeRequestAllowed(workingTimeRequest)) {
				this.editWorkingTimeRequest(workingTimeRequest);
			} else {
				this.showWorkingTimeRequest(workingTimeRequest);
			}
		});
	}

	private editWorkingTimeRequestAllowed(workingTimeRequest: AbstractWorkingTimeRequestResponseModel): boolean {
		// WTR cannot be edited if InApproval
		return workingTimeRequest.status === WorkingTimeRequestStatus.ApprovalRequested;
	}

	private editWorkingTimeRequest(workingTimeRequest: AbstractWorkingTimeRequestResponseModel) {
		this.workingTimeRequestDialog.edit(workingTimeRequest);
	}

	private showWorkingTimeRequest(workingTimeRequest: AbstractWorkingTimeRequestResponseModel) {
		this.workingTimeRequestDialog.show(workingTimeRequest);
	}

	private deleteWorkingTimeRequest(workingTimeRequest: AbstractWorkingTimeRequestResponseModel) {
		WorkingTimeRequestUtil.deleteWorkingTimeRequest(workingTimeRequest, () => this.reload());
	}

	private duplicateWorkingTimeRequest(workingTimeRequest: AbstractWorkingTimeRequestResponseModel) {
		if (WorkingTimeRequestUtil.isWorkingTimeNoticeRequest(workingTimeRequest)) {
			this.config.workingTimeNoticeDialog.openForDuplicate(noticeRequestDuplicateSource(workingTimeRequest));
		} else {
			this.workingTimeRequestDialog.duplicate(workingTimeRequest, true);
		}
	}

	private requestCancellationWorkingTimeRequest(workingTimeRequest: AbstractWorkingTimeRequestResponseModel) {
		WorkingTimeRequestUtil.requestCancellationWorkingTimeRequest(workingTimeRequest, () => this.reload());
	}

	private approveWorkingTimeRequest(workingTimeRequest: AbstractWorkingTimeRequestResponseModel) {
		WorkingTimeRequestUtil.handleWorkingTimeRequestAction(
			"APPROVAL",
			workingTimeRequest,
			() => this.reload(),
			"workingTimeRequestStatus.approve.title",
			"workingTimeRequestStatus.approve.message",
			WorkingTimeRequestEvent.APPROVE_REQUEST_STEP,
			workingTimeRequest.currentApprovableStep?.workingTimeRequestStepId,
			null, // TODO: Open dialog if needed
			"btn-success"
		);
	}

	private rejectWorkingTimeRequest(workingTimeRequest: AbstractWorkingTimeRequestResponseModel) {
		WorkingTimeRequestUtil.handleWorkingTimeRequestAction(
			"APPROVAL",
			workingTimeRequest,
			() => this.reload(),
			"workingTimeRequestStatus.reject.title",
			"workingTimeRequestStatus.reject.message",
			WorkingTimeRequestEvent.REJECT_REQUEST_STEP,
			workingTimeRequest.currentApprovableStep?.workingTimeRequestStepId,
			null, // TODO: Open dialog if needed
			"btn-danger"
		);
	}

	private approveCancellationWorkingTimeRequest(workingTimeRequest: AbstractWorkingTimeRequestResponseModel) {
		WorkingTimeRequestUtil.handleWorkingTimeRequestAction(
			"CANCELLATION",
			workingTimeRequest,
			() => this.reload(),
			"workingTimeRequestStatus.approveCancellation.title",
			"workingTimeRequestStatus.approveCancellation.message",
			WorkingTimeRequestEvent.APPROVE_CANCELLATION_STEP,
			workingTimeRequest.currentApprovableStep?.workingTimeRequestStepId,
			null, // TODO: Open dialog if needed
			"btn-success"
		);
	}

	private rejectCancellationWorkingTimeRequest(workingTimeRequest: AbstractWorkingTimeRequestResponseModel) {
		WorkingTimeRequestUtil.handleWorkingTimeRequestAction(
			"CANCELLATION",
			workingTimeRequest,
			() => this.reload(),
			"workingTimeRequestStatus.rejectCancellation.title",
			"workingTimeRequestStatus.rejectCancellation.message",
			WorkingTimeRequestEvent.REJECT_CANCELLATION_STEP,
			workingTimeRequest.currentApprovableStep?.workingTimeRequestStepId,
			null, // TODO: Open dialog if needed
			"btn-danger"
		);
	}
}
